import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useUpdateAccountMutation } from 'services/accounts';
import { useGetApiKeysListQuery } from 'services/apiKeys';
import { useUploadAssetMutation } from 'services/staticAssets';

import { FormFieldType } from 'components/form/formLayout/types';

import selectAccount from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountLandingPageSettings } from 'types/account';
import { ValidationErrorMeta } from 'types/error';
import { UserAccessLevel } from 'types/user';

import { getValidationDetails } from 'utils/error';

import { getFormValues } from '../../utils';
import { KEYS, LABELS, formFields } from './keys';
import { LandingSection } from './landing';

export const LandingSectionContainer = () => {
  const { t } = useTranslation();
  const { account } = useSelector(selectAccount);
  const accountData = getFormValues(account, formFields) as AccountLandingPageSettings;
  const [values, setValues] = useState<AccountLandingPageSettings>(accountData);
  const [previewSeed, setPreviewSeed] = useState<string>(Math.random().toString());
  const [saveAccountInfo, { isLoading: isUpdating, error, reset }] = useUpdateAccountMutation();
  const [uploadAsset, { isLoading: isAssetUploading, reset: resetUpload }] = useUploadAssetMutation();
  const userLevel = useSelector(selectUserLevel);
  const { data } = useGetApiKeysListQuery(account.uuid, {
    skip: !account.uuid,
  });
  const publicKey = data?.keys?.[0]?.publicKey;

  useEffect(() => {
    setValues(getFormValues(account, formFields) as AccountLandingPageSettings);
  }, [account]);

  const handleFormValueChange = (key: string, value: any) => {
    setValues({
      ...values,
      [key]: value,
    });
    reset();
    resetUpload();
  };

  const isAdmin = Boolean(userLevel && userLevel >= UserAccessLevel.Admin);
  const disabledFields = {
    isCertified: !isAdmin,
    processingFee: !isAdmin,
  };

  const readOnlyMode = Boolean(userLevel && userLevel === UserAccessLevel.Support);

  const resetFormValues = () => {
    setValues(getFormValues(account, formFields) as AccountLandingPageSettings);
    reset();
    resetUpload();
    setPreviewSeed(Math.random().toString());
  };

  const uploadFormFile = async (fieldName: keyof AccountLandingPageSettings): Promise<boolean | { [field: string]: string }> => {
    const isFileUpdated = !isEqual(account[fieldName], values[fieldName]);
    const isFile = values[fieldName] instanceof File;
    const shouldUploadFile = isFileUpdated && isFile;
    if (shouldUploadFile) {
      const response = await uploadAsset(values[fieldName] as File);
      if ('error' in response) {
        return false;
      }

      return { [fieldName]: response.data };
    }

    return true;
  };

  const uploadFormFiles = async (): Promise<boolean | { [field: string]: string }> => {
    const fileFields = formFields.filter((field) => field.type === FormFieldType.File);
    const promises = fileFields.map((field) => {
      const fieldName = field.key as keyof AccountLandingPageSettings;
      return uploadFormFile(fieldName);
    });

    const result = await Promise.all(promises);
    if (!result.every(Boolean)) {
      return false;
    }

    return result.reduce((output, response) => {
      if (typeof response === 'object') {
        return {
          ...(output as object),
          ...response,
        };
      }

      return output;
    }, {});
  };

  const saveFormValues = async () => {
    if (readOnlyMode) {
      return;
    }

    const changedValues = await uploadFormFiles();
    if (!changedValues) {
      return;
    }

    const payload: Partial<AccountLandingPageSettings> = {
      ...values,
      ...(changedValues as object),
    };

    const disabledFieldKeys = Object.entries(disabledFields)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => key);

    const diffValues = (Object.keys(payload) as (keyof AccountLandingPageSettings)[])
      .filter((key) => account[key] !== payload[key])
      .reduce((result, key) => ({ ...result, [key]: payload[key] }), { uuid: account.uuid });

    const preparedValues = omit(diffValues, disabledFieldKeys);
    await saveAccountInfo(preparedValues);
    setPreviewSeed(Math.random().toString());
    setValues({
      ...values,
      ...omit(preparedValues, KEYS.UUID),
    });
  };

  const isFormChanged = !isEqual(values, accountData);
  const saveErrorMeta = error ? ((error as FetchBaseQueryError).data as { meta: ValidationErrorMeta })?.meta : undefined;
  const saveError = saveErrorMeta ? getValidationDetails(saveErrorMeta) : undefined;
  const possibleFiles = formFields.reduce((output, field) => {
    if (field.type !== FormFieldType.File) {
      return output;
    }

    return {
      ...output,
      [field.key]: values[field.key as keyof AccountLandingPageSettings],
    };
  }, {});

  const fileErrors = Object.entries(possibleFiles).reduce((result, [key, value]) => {
    const shouldAddError = value instanceof File && value.size > KEYS.MAX_LOGO_SIZE;
    if (shouldAddError) {
      return {
        ...result,
        [key]: t(LABELS.FORM_FIELDS.ERRORS.MAX_FILE_SIZE),
      };
    }

    return result;
  }, {});
  const displayedError = saveError || fileErrors;
  const isSaveAvailable = isFormChanged && isEmpty(displayedError);

  return (
    <LandingSection
      account={values}
      initialValues={account}
      onFormValueChange={handleFormValueChange}
      isFormChanged={isFormChanged}
      isSaveAvailable={isSaveAvailable}
      resetFormValues={resetFormValues}
      saveFormValues={saveFormValues}
      disabledFields={disabledFields}
      error={displayedError}
      isUpdating={isUpdating || isAssetUploading}
      readOnlyMode={readOnlyMode}
      previewOptions={{
        publicKey,
        seed: previewSeed,
      }}
    />
  );
};
